import React, { useCallback, useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty } from "@/pages/shared";

const MONTH_KEYS = ["m_jan", "m_feb", "m_mar", "m_apr", "m_may", "m_jun", "m_jul", "m_aug", "m_sep", "m_oct", "m_nov", "m_dec"];

export default function AdminSeasonReport() {
  const { t } = useLang();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [first, setFirst] = useState(1);
  const [last, setLast] = useState(12);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/reports/season?year=${year}&first_month=${first}&last_month=${last}`);
      setData(d); setError("");
    } catch (e) {
      setData(null); setError(apiError(e.response?.data?.detail));
    }
  }, [year, first, last]);
  useEffect(() => { load(); }, [load]);

  const csv = () => {
    if (!data) return;
    const head = ["Maand", "Contant", "Op rekening", "Facturen", "Totaal", "Verkopen", "Kasverschil", "Dagen met verschil", "Afgestort"];
    const lines = data.months.map((m) => [m.month, m.cash.toFixed(2), m.account.toFixed(2), m.invoices.toFixed(2),
      m.total.toFixed(2), m.sales_count, m.difference.toFixed(2), m.days_with_diff, m.banked.toFixed(2)]);
    const staff = data.staff.map((s) => [s.name, s.number, s.hours.toFixed(2), s.revenue.toFixed(2), s.sales_count]);
    const all = [head, ...lines, [], ["Medewerker", "Nummer", "Uren", "Omzet", "Verkopen"], ...staff];
    const blob = new Blob([all.map((l) => l.join(";")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `jaaroverzicht-${year}.csv`;
    a.click();
  };

  return (
    <Panel
      title={t("admin_season")}
      testid="season-panel"
      action={
        <div className="flex flex-wrap items-end gap-2 print:hidden">
          <div className="space-y-1">
            <Label className="text-xs">{t("season_year")}</Label>
            <Input type="number" min="2000" max="2100" value={year} className="w-24" data-testid="season-year"
              onChange={(e) => setYear(Number(e.target.value))} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("season_from")}</Label>
            <select value={first} onChange={(e) => setFirst(Number(e.target.value))} data-testid="season-first"
              className="h-9 rounded-md border border-border bg-background px-2 text-sm">
              {MONTH_KEYS.map((k, i) => <option key={k} value={i + 1}>{t(k)}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("season_to")}</Label>
            <select value={last} onChange={(e) => setLast(Number(e.target.value))} data-testid="season-last"
              className="h-9 rounded-md border border-border bg-background px-2 text-sm">
              {MONTH_KEYS.map((k, i) => <option key={k} value={i + 1}>{t(k)}</option>)}
            </select>
          </div>
          <Button variant="outline" onClick={csv} data-testid="season-csv">CSV</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="season-print"><Printer className="h-4 w-4 mr-1" />{t("print")}</Button>
        </div>
      }
    >
      <div className="p-5 space-y-5">
        <p className="text-sm text-muted-foreground">{t("season_desc")}</p>
        {error && <p className="text-sm text-rose-600" data-testid="season-error">{error}</p>}

        {data && (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[["total", t("total"), money(data.totals.total)],
                ["cash", t("pay_cash"), money(data.totals.cash)],
                ["difference", t("cash_difference"), money(data.totals.difference)],
                ["hours", t("hours_worked"), `${data.totals.hours.toFixed(2)}u`]].map(([k, label, value]) => (
                <div key={k} className="rounded-xl border border-border p-4" data-testid={`season-kpi-${k}`}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
                  <p className={`font-display font-extrabold text-2xl tabular-nums ${k === "difference" && data.totals.difference ? "text-rose-600" : ""}`}>{value}</p>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto">
              <Table data-testid="season-months">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("month")}</TableHead>
                    <TableHead className="text-right">{t("pay_cash")}</TableHead>
                    <TableHead className="text-right">{t("pay_account")}</TableHead>
                    <TableHead className="text-right">{t("invoices")}</TableHead>
                    <TableHead className="text-right">{t("total")}</TableHead>
                    <TableHead className="text-right">{t("sales")}</TableHead>
                    <TableHead className="text-right">{t("cash_difference")}</TableHead>
                    <TableHead className="text-right">{t("cash_deposit")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.months.map((m) => (
                    <TableRow key={m.month} data-testid={`season-month-${m.month}`}>
                      <TableCell className="font-medium">{t(MONTH_KEYS[Number(m.month.slice(5)) - 1])}</TableCell>
                      <TableCell className="text-right tabular-nums text-emerald-600">{money(m.cash)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sky-600">{money(m.account)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{money(m.invoices)}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{money(m.total)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{m.sales_count}</TableCell>
                      <TableCell className={`text-right tabular-nums ${m.difference ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>
                        {m.difference ? money(m.difference) : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{m.banked ? money(m.banked) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">{t("season_staff")}</p>
              {data.staff.length === 0 ? <Empty /> : (
                <div className="overflow-x-auto">
                  <Table data-testid="season-staff">
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("seller")}</TableHead>
                        <TableHead className="text-right">{t("hours_worked")}</TableHead>
                        <TableHead className="text-right">{t("pay_cash")}</TableHead>
                        <TableHead className="text-right">{t("pay_account")}</TableHead>
                        <TableHead className="text-right">{t("invoices")}</TableHead>
                        <TableHead className="text-right">{t("total")}</TableHead>
                        <TableHead className="text-right">{t("sales")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.staff.map((s) => (
                        <TableRow key={`${s.name}-${s.number}`} data-testid={`season-staff-${s.number || s.name}`}>
                          <TableCell className="font-medium whitespace-nowrap">
                            {s.name}
                            {s.number && <span className="block text-[11px] text-muted-foreground tabular-nums">{s.number}</span>}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{s.hours.toFixed(2)}u</TableCell>
                          <TableCell className="text-right tabular-nums text-emerald-600">{money(s.cash)}</TableCell>
                          <TableCell className="text-right tabular-nums text-sky-600">{money(s.account)}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">{money(s.invoices)}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold">{money(s.revenue)}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">{s.sales_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}
